import { useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Moon, MoreHorizontal, Pin, PinOff } from 'lucide-react';
import { useToast } from '@/shared/ui/Toast';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { useSetPracticeSnooze } from '../../hooks/useCommandCenter';
import { PINNED_PRACTICES_MAX, useTogglePinnedPractice } from '../../hooks/usePinnedPractices';
import styles from './PracticeActions.module.css';

/**
 * The one door into a practice's own account. Every entry point (the table
 * row, the practice header) renders this same control, with the same list of
 * places to land, so nobody ends up in a client account by accident and the
 * impersonation is always the audited one.
 */
const DESTINATIONS = [
  { path: '/', label: 'Dashboard' },
  { path: '/strategy', label: 'Strategy' },
  { path: '/campaigns', label: 'Ad campaigns' },
  { path: '/seo', label: 'Online visibility' },
  { path: '/seo/reviews', label: 'Reviews' },
  { path: '/settings/paid', label: 'Paid Settings' },
  { path: '/settings/organic', label: 'Organic & Offline Settings' },
] as const;

export function ViewAsMenu({
  practiceId,
  practiceName,
  size = 'md',
}: {
  practiceId: string;
  practiceName: string;
  size?: 'sm' | 'md';
}) {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const [open, setOpen] = useState(false);

  const enter = (path: string) => {
    setOpen(false);
    startImpersonation({ id: practiceId, name: practiceName });
    navigate(path);
  };

  return (
    <div className={styles.menuWrap}>
      <button
        type="button"
        className={clsx(styles.viewAsButton, size === 'sm' && styles.viewAsButtonSm)}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Open ${practiceName}'s own dashboard`}
      >
        View as practice
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.menu} role="menu">
            <div className={styles.menuHeading}>Enter their session at</div>
            {DESTINATIONS.map((destination) => (
              <button
                key={destination.path}
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => enter(destination.path)}
              >
                {destination.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The row / header overflow menu: pin, the story for the client email, snooze,
 * and the two deep tabs. Pins are the signed-in admin's own — `pinned` and
 * `pinnedCount` come from their list, never from the agency.
 */
export function PracticeMenu({
  practiceId,
  practiceName,
  snoozed,
  story,
  pinned = false,
  pinnedCount = 0,
}: {
  practiceId: string;
  practiceName: string;
  snoozed: boolean;
  story: string[];
  pinned?: boolean;
  pinnedCount?: number;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const setSnooze = useSetPracticeSnooze();
  const togglePin = useTogglePinnedPractice();
  const [open, setOpen] = useState(false);
  const pinFull = !pinned && pinnedCount >= PINNED_PRACTICES_MAX;

  const togglePinned = async () => {
    setOpen(false);
    try {
      await togglePin.mutateAsync({ practiceId, pinned: !pinned });
      toast.success(pinned ? `${practiceName} unpinned` : `${practiceName} pinned to the top`);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
        ?.data?.error?.message;
      toast.error(message ?? (pinned ? 'Could not unpin' : 'Could not pin'));
    }
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const copyStory = async () => {
    setOpen(false);
    const text = `${practiceName}\n\n${story.map((l) => `• ${l}`).join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied this week’s story for the client email');
    } catch {
      toast.error('Could not copy — your browser blocked clipboard access');
    }
  };

  const toggleSnooze = async () => {
    setOpen(false);
    try {
      await setSnooze.mutateAsync({ practiceId, snoozed: !snoozed });
      toast.success(snoozed ? `${practiceName} unsnoozed` : `${practiceName} snoozed`);
    } catch {
      toast.error(snoozed ? 'Could not unsnooze' : 'Could not snooze');
    }
  };

  return (
    <div className={styles.menuWrap}>
      <button
        type="button"
        className={clsx(styles.iconButton, snoozed && styles.iconButtonSnoozed)}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${practiceName}`}
      >
        {snoozed ? <Moon size={13} aria-hidden /> : <MoreHorizontal size={14} aria-hidden />}
      </button>
      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <div className={styles.menu} role="menu">
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={togglePinned}
              disabled={pinFull || togglePin.isPending}
              title={pinFull ? `You already have ${PINNED_PRACTICES_MAX} pinned` : undefined}
            >
              {pinned ? (
                <>
                  <PinOff size={12} aria-hidden /> Unpin from top
                </>
              ) : (
                <>
                  <Pin size={12} aria-hidden /> Pin to top
                  {pinFull && (
                    <span className={styles.menuHint}>{PINNED_PRACTICES_MAX} already</span>
                  )}
                </>
              )}
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={copyStory}
              disabled={story.length === 0}
            >
              Copy this week&apos;s story
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={toggleSnooze}
              disabled={setSnooze.isPending}
            >
              {snoozed ? (
                <>
                  <Check size={12} aria-hidden /> Unsnooze practice
                </>
              ) : (
                <>
                  <Moon size={12} aria-hidden /> Snooze practice
                </>
              )}
            </button>
            <div className={styles.menuDivider} />
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => go(`/agency/practices/${practiceId}?tab=settings`)}
            >
              Practice settings
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => go(`/agency/practices/${practiceId}?tab=agents`)}
            >
              Agents
            </button>
          </div>
        </>
      )}
    </div>
  );
}
